import { useMemo } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import {
  SimsDaoHologramSimIds,
  type HandlersHologramSimIdsResponse,
  type StructsSimRequestProperties,
} from 'types/__generated__/SimInventoryApi';
import type { HologramSimIds, HologramSimIdsMap } from 'types/common';
import postFetcher from '../utils/postFetcher';
import { ApiBaseError } from '../utils/shared';

export interface SimIdsResultError extends ApiBaseError {}

export const DEFAULT_PAGINATION = 50 as const;

interface UseSimIdsProps {
  filters: StructsSimRequestProperties;
  canLoad: boolean;
  onLoadingSlow?: SimIdsSWRConfiguration['onLoadingSlow'];
}

type SimIdsSWRConfiguration = SWRConfiguration<HandlersHologramSimIdsResponse, SimIdsResultError>;

const isValidSimIdSet = (sim: SimsDaoHologramSimIds): sim is HologramSimIds => !!sim.hologramSimId;

const useSimsIds = ({ filters, canLoad, onLoadingSlow }: UseSimIdsProps) => {
  const key = canLoad
    ? {
        url: `${process.env.VITE_BIZ_API_ROOT}/sim-inventory/sims/ids`,
        body: {
          ...filters,
        },
      }
    : null;

  const config: SimIdsSWRConfiguration = useSWRConfig();

  if (onLoadingSlow) {
    config.onLoadingSlow = onLoadingSlow;
  }

  const { data, error, isLoading, isValidating, mutate } = useSWR<
    HandlersHologramSimIdsResponse,
    SimIdsResultError
  >(key, postFetcher, config);

  const simIds = useMemo<HologramSimIdsMap>(
    () =>
      new Map(
        data?.hologramSimIds
          ?.filter(isValidSimIdSet)
          ?.map((sim) => [sim.hologramSimId, sim.claimed_device_id])
      ),
    [data]
  );

  return {
    simIds,
    error,
    isLoading,
    isValidating,
    mutate,
  };
};

export default useSimsIds;
